import { Tables } from "@/lib/supabase/supabase_types_override";

/**
 * Determines which questions must be synced together due to conditional dependencies.
 *
 * For each question with conditionals, this function identifies all questions that must
 * also be synced to maintain valid conditional logic. A question needs to be linked if:
 * - It's referenced in another question's conditionals
 * - The referenced question has dangerous changes (e.g., field_type changed)
 *
 * @param templateQuestions - Questions from the survey template
 * @param instanceQuestions - Questions from the survey instance
 * @param questionResults - Results from dangerous change checks
 * @returns Record where key is question ID and value is array of question IDs that must be synced together
 */
export function determineLinkedQuestions(
  templateQuestions: Tables<"survey_bank_question_bank">[],
  instanceQuestions: Tables<"question">[],
  questionResults: Array<{
    instanceQuestion: Tables<"question"> | null;
    templateQuestion: Tables<"survey_bank_question_bank"> | null;
    dangerousChanges: any[];
  }>
): Record<Tables<"survey_bank_question_bank">['id'], Tables<"survey_bank_question_bank">['id'][]> {
  const linkedQuestions: Record<Tables<"survey_bank_question_bank">['id'], Tables<"survey_bank_question_bank">['id'][]> = {};

  // Build maps for template <-> instance ID relationships
  const templateRefToInstanceId = new Map<string, string>();
  const instanceIdToTemplateRef = new Map<string, string>();
  instanceQuestions.forEach(q => {
    if (q.survey_bank_question_bank_ref_id) {
      templateRefToInstanceId.set(q.survey_bank_question_bank_ref_id, q.id);
      instanceIdToTemplateRef.set(q.id, q.survey_bank_question_bank_ref_id);
    }
  });

  // Build a map of template questions that have dangerous changes (via their instances)
  const templateQuestionsWithDangerousChanges = new Set<string>();
  questionResults.forEach(result => {
    if (result.dangerousChanges.length > 0 && result.templateQuestion) {
      templateQuestionsWithDangerousChanges.add(result.templateQuestion.id);
    }
  });

  // Extract all question IDs referenced in conditionals
  const extractReferencedIds = (obj: any, referencedIds: Set<string> = new Set()): Set<string> => {
    if (Array.isArray(obj)) {
      obj.forEach(item => extractReferencedIds(item, referencedIds));
    } else if (typeof obj === "object" && obj !== null) {
      Object.entries(obj).forEach(([key, value]) => {
        // Check if this key looks like a question ID (UUID pattern)
        const isQuestionId = typeof key === 'string' &&
          key.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);

        if (isQuestionId) {
          referencedIds.add(key);
        }

        // Recursively check nested structures (for AND/OR operators)
        if (typeof value === 'object' && value !== null) {
          extractReferencedIds(value, referencedIds);
        }
      });
    }
    return referencedIds;
  };

  // Check template questions for conditionals
  templateQuestions.forEach(templateQuestion => {
    const conditionals = templateQuestion.selected_configuration?.conditionals;
    if (!conditionals) return;

    const referencedTemplateIds = extractReferencedIds(conditionals);
    const linkedTemplateIds: string[] = [];

    // For each referenced template question, check if it has dangerous changes
    referencedTemplateIds.forEach(refId => {
      if (templateQuestionsWithDangerousChanges.has(refId)) {
        linkedTemplateIds.push(refId);
      }
    });

    // If this template question has linked dependencies, add to result
    if (linkedTemplateIds.length > 0) {
      linkedQuestions[templateQuestion.id] = linkedTemplateIds;
    }
  });

  // Check instance questions for conditionals and map back to template IDs
  instanceQuestions.forEach(instanceQuestion => {
    const conditionals = instanceQuestion.selected_configuration?.conditionals;
    if (!conditionals) return;

    // Get the template ID for this instance question
    const templateQuestionId = instanceIdToTemplateRef.get(instanceQuestion.id);
    if (!templateQuestionId) return; // Skip if no template reference

    const referencedInstanceIds = extractReferencedIds(conditionals);
    const linkedTemplateIds: string[] = [];

    // Map referenced instance IDs back to template IDs and check for dangerous changes
    referencedInstanceIds.forEach(refInstanceId => {
      const refTemplateId = instanceIdToTemplateRef.get(refInstanceId);
      if (refTemplateId && templateQuestionsWithDangerousChanges.has(refTemplateId)) {
        linkedTemplateIds.push(refTemplateId);
      }
    });

    // If this question has linked dependencies, merge with existing or create new entry
    if (linkedTemplateIds.length > 0) {
      if (linkedQuestions[templateQuestionId]) {
        // Merge with existing entries, avoiding duplicates
        const existing = new Set(linkedQuestions[templateQuestionId]);
        linkedTemplateIds.forEach(id => existing.add(id));
        linkedQuestions[templateQuestionId] = Array.from(existing);
      } else {
        linkedQuestions[templateQuestionId] = linkedTemplateIds;
      }
    }
  });

  return linkedQuestions;
}